// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    Field,
    Input,
    makeStyles,
    RadioGroup,
    shorthands,
    Textarea,
} from '@fluentui/react-components';
import { CoursePlatform, Curriculum } from '@microsoft/vscodeedu-api';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Html5ColorIcon, PythonColorIcon } from '../../icons/code';
import { MaxDescriptionLength, MaxTitleLength } from '../../models';
import { useAppStyles } from '../../styles';
import { getCoursePlatformText, IntlProps, RadioCard, TrackContext } from '../common';
import { CancelButton, ConfirmButton, DialogHeader } from '../dialogs';

const useStyles = makeStyles({
    templateList: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        ...shorthands.gap('0.5rem'),
        marginBottom: '1rem',
    },
});

// Create activity dialog props.
export type CreateActivityDialogProps = IntlProps<
    Partial<DialogProps> & {
        onCreate: (props: Pick<Curriculum, 'title' | 'description' | 'platform'>) => void;
    }
>;

// Create activity dialog.
export default injectIntl((props: CreateActivityDialogProps) => {
    const { intl, onCreate } = props;
    const appStyles = useAppStyles();
    const styles = useStyles();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [platform, setPlatform] = useState<CoursePlatform | undefined>(undefined);

    const onConfirm = useCallback(() => {
        const trimmedDescription = description.trim();
        onCreate({
            platform: platform!,
            title: title.trim(),
            description: trimmedDescription.length > 0 ? trimmedDescription : undefined,
        });
    }, [description, onCreate, platform, title]);

    return (
        <TrackContext.Provider value={{ container: 'create-activity-dialog' }}>
            <Dialog {...props}>
                <DialogSurface className={appStyles.dialogNarrow}>
                    <DialogBody>
                        <DialogHeader
                            title={intl.formatMessage({
                                defaultMessage: 'Create an activity',
                                description: 'Create activity dialog title.',
                            })}
                        />
                        <DialogContent>
                            <Field
                                className={appStyles.dialogField}
                                required
                                label={intl.formatMessage({
                                    defaultMessage: 'Activity title',
                                    description: 'Label for the activity title text box.',
                                })}
                            >
                                <Input
                                    value={title}
                                    onChange={(_, { value }) => setTitle(value)}
                                    maxLength={MaxTitleLength}
                                    placeholder={intl.formatMessage({
                                        defaultMessage: 'Name your activity',
                                        description: 'Placeholder text inside the activity title text box.',
                                    })}
                                />
                            </Field>
                            <Field
                                className={appStyles.dialogField}
                                label={intl.formatMessage({
                                    defaultMessage: 'Description (optional)',
                                    description: 'Label for the activity description text box.',
                                })}
                            >
                                <Textarea
                                    value={description}
                                    onChange={(_, { value }) => setDescription(value)}
                                    maxLength={MaxDescriptionLength}
                                    placeholder={intl.formatMessage({
                                        defaultMessage: 'Tell us about your activity...',
                                        description: 'Placeholder text inside the activity description text box.',
                                    })}
                                />
                            </Field>
                            <Field
                                className={appStyles.dialogField}
                                required
                                label={intl.formatMessage({
                                    defaultMessage: 'Select a template',
                                    description: 'Label for template selection radio group.',
                                })}
                            >
                                <RadioGroup
                                    className={styles.templateList}
                                    value={platform}
                                    onChange={(_, { value }) => setPlatform(value as CoursePlatform)}
                                >
                                    <RadioCard
                                        key="python"
                                        value={'python'}
                                        icon={<PythonColorIcon />}
                                        title={getCoursePlatformText('python')}
                                        description={
                                            <FormattedMessage
                                                defaultMessage="Python programming course."
                                                description="Course type description."
                                            />
                                        }
                                        selected={platform === 'python'}
                                    />
                                    <RadioCard
                                        key="html"
                                        value={'html'}
                                        icon={<Html5ColorIcon />}
                                        title={getCoursePlatformText('html')}
                                        description={
                                            <FormattedMessage
                                                defaultMessage="Web development course."
                                                description="Course type description."
                                            />
                                        }
                                        selected={platform === 'html'}
                                    />
                                </RadioGroup>
                            </Field>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton
                                appearance="primary"
                                disabled={!(title.trim() && platform !== undefined)}
                                onClick={onConfirm}
                            >
                                <FormattedMessage
                                    description="Create new activity dialog confirm button."
                                    defaultMessage="Create activity"
                                />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
});
